import { ErrorType } from 'data/App/types'
import { ApiKeyCredentialsType, ApiKeyType, SessionType, TOtpConfigType } from 'data/types'

import * as AT from './actionTypes'
import { actions as extraKYCQuestions } from './ExtraKYCQuestions/slice'
import * as localSettings from './localSettings/actions'
import { actions as secondFactorChallenge } from './SecondFactorChallenge/slice'
import { actions as settings } from './Settings/slice'
import {
  AccumulatorType,
  DeviceInfoType,
  TiersLimitsVolumeType,
  TwoFAType,
  UserFeesType,
  UserSettingsType,
  UserType
} from './types'
import { actions as ukFrictions } from './UKFrictions/slice'

export type SetUserActionType = {
  payload: { readonly error?: ErrorType; readonly user?: UserType }
  type: typeof AT.SET_USER
}

export type AuthedActionType = {
  payload: { readonly authenticated: boolean }
  type: typeof AT.AUTHED
}

export type SetSocketAuthedActionType = {
  payload: { readonly authenticated: boolean }
  type: typeof AT.SET_SOCKET_AUTHED
}

export type SignoutActionType = { type: typeof AT.SIGNOUT }

export type UpdateFillReceptPreferenceType = {
  type: typeof AT.SET_FILL_RECEIPT_PREFERENCE
}
export type SetUserSettingsType = {
  payload: {
    readonly prevSettings: Partial<UserSettingsType>
    readonly settings: Partial<UserSettingsType>
  }
  type: typeof AT.SET_USER_SETTINGS
}

export type SetDeviceInfoType = {
  payload: { readonly deviceInfo: DeviceInfoType }
  type: typeof AT.SET_DEVICE_INFO
}

export type AuthActionType =
  | AuthedActionType
  | SetSocketAuthedActionType
  | SetDeviceInfoType
  | { type: typeof AT.CHECK_SESSION }
  | { type: typeof AT.GET_USER }
  | { type: typeof AT.GET_USER_LIMITS }
  | { type: typeof AT.GET_USER_FEES }
  | { type: typeof AT.POLL_USER }
  | { type: typeof AT.REMOVE_TOTP_CONFIG }
  | {
      payload: { readonly keys: Array<ApiKeyType> }
      type: typeof AT.SET_API_KEYS
    }
  | {
      payload: { readonly keyToDeactivate: string }
      type: typeof AT.SET_API_KEY_DEACTIVATE
    }
  | {
      payload: { readonly emailVerificationId: string }
      type: typeof AT.SET_EMAIL_VERIFICATION_ID
    }
  | {
      payload: { readonly credentials: ApiKeyCredentialsType }
      type: typeof AT.SET_NEW_API_KEY_CREDENTIALS
    }
  | {
      payload: { readonly referrerUsername: string }
      type: typeof AT.SET_REFERRER_USERNAME
    }
  | {
      payload: { readonly session: SessionType }
      type: typeof AT.SET_SESSION
    }
  | {
      payload: { readonly tOtpConfig: TOtpConfigType }
      type: typeof AT.SET_TOTP_CONFIG
    }
  | {
      payload: { readonly status: Array<TwoFAType> }
      type: typeof AT.SET_TWO_FACTOR_STATUS
    }
  | SetUserActionType
  | SetUserSettingsType
  | {
      payload: { readonly limits: TiersLimitsVolumeType }
      type: typeof AT.SET_TIERS_LIMITS_VOLUME
    }
  | {
      payload: { readonly fees: UserFeesType }
      type: typeof AT.SET_USER_FEES
    }
  | {
      payload: { readonly aggregatedFees: Array<AccumulatorType> }
      type: typeof AT.SET_USER_AGGREGATED_FEES
    }
  | {
      payload: { readonly fees: UserFeesType }
      type: typeof AT.SET_USER_PROMOS
    }
  | SignoutActionType
  | UpdateFillReceptPreferenceType
  | {
      payload: boolean
      type: typeof AT.IS_AUTHING
    }

const authed = (authenticated: boolean): AuthActionType => ({
  payload: { authenticated },
  type: AT.AUTHED
})

const checkSession = (): AuthActionType => ({
  type: AT.CHECK_SESSION
})

const getUser = (): AuthActionType => ({
  type: AT.GET_USER
})

const pollUser = (): AuthActionType => ({
  type: AT.POLL_USER
})

const getTiersLimitsVolume = (): AuthActionType => ({
  type: AT.GET_USER_LIMITS
})

const getUserFees = (): AuthActionType => ({
  type: AT.GET_USER_FEES
})

const removeTotpConfig = (): AuthActionType => ({
  type: AT.REMOVE_TOTP_CONFIG
})

const setApiKeys = (keys: Array<ApiKeyType>): AuthActionType => ({
  payload: { keys },
  type: AT.SET_API_KEYS
})

const setApiKeyToDeactivate = (keyToDeactivate: string): AuthActionType => ({
  payload: { keyToDeactivate },
  type: AT.SET_API_KEY_DEACTIVATE
})

const setEmailVerificationId = (emailVerificationId: string): AuthActionType => ({
  payload: { emailVerificationId },
  type: AT.SET_EMAIL_VERIFICATION_ID
})

const setNewApiKeyCredentials = (credentials: ApiKeyCredentialsType): AuthActionType => {
  return {
    payload: { credentials },
    type: AT.SET_NEW_API_KEY_CREDENTIALS
  }
}

const setReferrerUsername = (referrerUsername: string): AuthActionType => ({
  payload: { referrerUsername },
  type: AT.SET_REFERRER_USERNAME
})

const setSession = (session: SessionType): AuthActionType => ({
  payload: { session },
  type: AT.SET_SESSION
})

const setSocketAuthed = (authenticated: boolean): AuthActionType => ({
  payload: { authenticated },
  type: AT.SET_SOCKET_AUTHED
})

const setTotpConfig = (tOtpConfig: TOtpConfigType): AuthActionType => ({
  payload: { tOtpConfig },
  type: AT.SET_TOTP_CONFIG
})

const setTwoFactorStatus = (status: Array<TwoFAType>): AuthActionType => ({
  payload: { status },
  type: AT.SET_TWO_FACTOR_STATUS
})

const setUser = (response: {
  readonly error?: ErrorType
  readonly user?: UserType
}): AuthActionType => ({
  payload: response,
  type: AT.SET_USER
})

const setUserSettings = (
  prevSettings: Partial<UserSettingsType>,
  settings: Partial<UserSettingsType>
): AuthActionType => ({
  payload: { prevSettings, settings },
  type: AT.SET_USER_SETTINGS
})

const updateFillReceptPreference = (): AuthActionType => ({
  type: AT.SET_FILL_RECEIPT_PREFERENCE
})

const setUserFees = (fees: UserFeesType): AuthActionType => ({
  payload: { fees },
  type: AT.SET_USER_FEES
})

const setUserAggregateFees = (aggregatedFees: Array<AccumulatorType>): AuthActionType => ({
  payload: { aggregatedFees },
  type: AT.SET_USER_AGGREGATED_FEES
})

const setUserPromos = (fees: UserFeesType): AuthActionType => ({
  payload: { fees },
  type: AT.SET_USER_PROMOS
})

const setUserLimits = (limits: TiersLimitsVolumeType): AuthActionType => ({
  payload: { limits },
  type: AT.SET_TIERS_LIMITS_VOLUME
})

const signout = (): AuthActionType => ({
  type: AT.SIGNOUT
})

const signupStart = () => ({
  type: AT.SIGNUP_START
})

const isAuthing = (authing: boolean) => ({
  payload: authing,
  type: AT.IS_AUTHING
})

export default {
  authed,
  checkSession,
  extraKYCQuestions,
  getTiersLimitsVolume,
  getUser,
  getUserFees,
  isAuthing,
  localSettings,
  pollUser,
  removeTotpConfig,
  secondFactorChallenge,
  setApiKeyToDeactivate,
  setApiKeys,
  setEmailVerificationId,
  setNewApiKeyCredentials,
  setReferrerUsername,
  setSession,
  setSocketAuthed,
  setTotpConfig,
  setTwoFactorStatus,
  setUser,
  setUserAggregateFees,
  setUserFees,
  setUserLimits,
  setUserPromos,
  setUserSettings,
  settings,
  signout,
  signupStart,
  ukFrictions,
  updateFillReceptPreference
}
